import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Menu, X, Search } from "lucide-react";
import { Input } from "./ui/input";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";

const searchablePages = [
  { title: "KI-Value-Engine", description: "Portfolio-Steuerung, Value-Dashboard, Operating Rhythm", href: "/ki-value-engine" },
  { title: "KI-Value-Check", description: "7-Fragen-Assessment zum KI-Reifegrad", href: "/ki-value-check" },
  { title: "Value Dashboard", description: "Echtzeit-Übersicht KI-Investitionen und Wertbeitrag", href: "/value-dashboard" },
  { title: "ROI & Business Impact", description: "Messbare Ergebnisse und EBIT-Impact", href: "/roi-business-impact" },
  { title: "AI Driven Enterprise", description: "Vom Pilotprojekt zum AI Driven Enterprise, 4+1 System", href: "/ai-driven-enterprise" },
  { title: "KI-Transformation", description: "Strategische KI-Transformationsberatung", href: "/leistungsangebot/ki-transformation" },
  { title: "Private Equity", description: "KI-Lösungen für PE und Portfolio-Unternehmen", href: "/private-equity" },
  { title: "KI-Akademie", description: "Schulungen und Weiterbildung für Führungskräfte", href: "/leistungsangebot/ki-akademie" },
  { title: "KI-Workshops", description: "Interaktive KI-Workshops für Teams", href: "/leistungsangebot/ki-workshops" },
  { title: "KI-Coaching", description: "C-Level Executive Coaching für KI-Strategie", href: "/leistungsangebot/ki-coaching" },
  { title: "Wissenscenter", description: "White Papers, Studien und Ressourcen", href: "/wissenscenter" },
  { title: "KI Use Cases", description: "Anwendungsfälle und Fallstudien", href: "/ai-use-cases" },
  { title: "Über uns", description: "BROVKO GmbH, Team und Unternehmensgeschichte", href: "/ueber-uns" },
  { title: "Souveräne Cloud", description: "DSGVO-konforme KI-Infrastruktur, STACKIT", href: "/souveraene-cloud" },
  { title: "AIWizz Plattform", description: "Enterprise-KI-Plattform für Unternehmen", href: "/aiwizz-plattform" },
  { title: "Partnerprogramm", description: "IT-Partner und Kooperationen", href: "/partnerprogramm" },
  { title: "Produkte", description: "Eigene Produkte der BROVKO GmbH, von Granalis bis Fairspark", href: "/produkte" },
  { title: "Granalis", description: "Geprüftes Wissen aus unstrukturierten Daten, Enterprise Knowledge Platform", href: "/granalis" },
  { title: "Spin-Offs", description: "Ausgründungen und Tochterunternehmen", href: "/spin-offs" },
  { title: "Kontakt", description: "Kontaktformular, E-Mail, Telefon", href: "/contact" },
  { title: "Impressum", description: "Rechtliche Angaben BROVKO GmbH", href: "/imprint" },
  { title: "Datenschutz", description: "Datenschutzerklärung DSGVO", href: "/privacy" },
];
const BRAND_NAV_CTA = "/ki-value-check";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const searchRef = useRef(null);
  const navRef = useRef(null);
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    {
      label: t('nav.valueEngine'),
      href: "/ki-value-engine",
      dropdown: [
        { label: t('nav.dropdown.theSystem'), href: "/ki-value-engine" },
        { label: t('nav.dropdown.valueCheck'), href: "/ki-value-check" },
        { label: t('nav.dropdown.valueDashboard'), href: "/value-dashboard" },
        { label: t('nav.dropdown.sovereignCloud'), href: "/souveraene-cloud" },
      ]
    },
    {
      label: t('nav.solutions'),
      href: "/loesungen",
      dropdown: [
        { label: t('nav.dropdown.roiImpact'), href: "/roi-business-impact" },
        { label: "AI Driven Enterprise", href: "/ai-driven-enterprise" },
        { label: t('nav.dropdown.transformation'), href: "/leistungsangebot/ki-transformation" },
        { label: t('nav.dropdown.privateEquity'), href: "/private-equity" },
      ]
    },
    {
      label: t('nav.enablement'),
      href: "/enablement",
      dropdown: [
        { label: t('nav.dropdown.academy'), href: "/leistungsangebot/ki-akademie" },
        { label: t('nav.dropdown.workshops'), href: "/leistungsangebot/ki-workshops" },
        { label: t('nav.dropdown.executiveCoaching'), href: "/leistungsangebot/ki-coaching" },
      ]
    },
    {
      // Produkte der BROVKO GmbH. Uebersicht und Granalis liegen auf brovko.de,
      // die uebrigen fuenf haben eigene Seiten.
      label: t('nav.products'),
      href: "/produkte",
      dropdown: [
        { label: t('nav.dropdown.allProducts'), href: "/produkte" },
        { label: t('nav.dropdown.granalis'), href: "/granalis" },
        { label: t('nav.dropdown.vox2doc'),   href: "https://vox2doc.de",     external: true },
        { label: t('nav.dropdown.domicus'),   href: "https://domicus.de",     external: true },
        { label: t('nav.dropdown.fleetdoc'),  href: "https://fleetdoc.de",    external: true },
        { label: t('nav.dropdown.fairspark'), href: "https://fairspark.de",   external: true },
        { label: t('nav.dropdown.replyly'),   href: "https://replyly.online", external: true },
      ]
    },
    {
      label: t('nav.expertise'),
      href: "/expertise",
      dropdown: [
        { label: t('nav.dropdown.whitepapersStudies'), href: "/wissenscenter" },
        { label: t('nav.dropdown.useCases'), href: "/ai-use-cases" },
        { label: t('nav.dropdown.aiwizzPlatform'), href: "/aiwizz-plattform" },
        { label: "Spin-Offs", href: "/spin-offs" },
        { label: t('nav.about'), href: "/ueber-uns" },
      ]
    },
  ];

  /* Klapppanel und Suche schliessen bei Klick ausserhalb oder Escape */
  useEffect(() => {
    const onDown = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowResults(false);
      if (navRef.current && !navRef.current.contains(e.target)) setOpenDropdown(null);
    };
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setOpenDropdown(null);
      setShowResults(false);
      setIsMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  /* Bei Seitenwechsel alles zuklappen */
  useEffect(() => {
    setOpenDropdown(null);
    setIsMenuOpen(false);
    setShowResults(false);
  }, [location.pathname]);

  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    const q = searchQuery.toLowerCase();
    setSearchResults(
      searchablePages.filter(
        (p) => p.title.toLowerCase().includes(q) || p.description.toLowerCase().includes(q)
      ).slice(0, 6)
    );
    setShowResults(true);
  }, [searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchResults.length > 0) handleResultClick(searchResults[0].href);
  };

  const handleResultClick = (href) => {
    navigate(href);
    setSearchQuery("");
    setShowResults(false);
  };

  const handleHomeClick = (e) => {
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const isActive = (item) =>
    location.pathname === item.href ||
    (item.dropdown || []).some((d) => !d.external && location.pathname === d.href);

  const linkClasses =
    "block px-3 py-2.5 rounded-lg text-sm text-ink-muted hover:text-ink hover:bg-white/[0.06] transition-colors duration-200";

  return (
    <header className="sticky top-0 z-50 bg-surface/90 backdrop-blur-xl border-b border-hairline">
      <div className="max-w-[1320px] mx-auto px-6">

        {/* ---- Obere Zeile: Wortmarke mit Claim, rechts Suche, Sprache, CTA ---- */}
        <div className="h-[68px] flex items-center gap-4">
          <Link to="/" onClick={handleHomeClick} className="flex-none group">
            <span className="block text-[22px] leading-none font-extrabold tracking-[0.02em] text-ink transition-colors group-hover:text-brand-400">
              BROVKO
            </span>
            <span className="mt-1 block text-[11px] font-bold leading-none tracking-[0.02em] text-ink-muted">
              The AI Transformation Company
            </span>
          </Link>

          <div className="ml-auto flex flex-none items-center gap-2">
            {/* Suche */}
            <div className="relative hidden md:block" ref={searchRef}>
              <form onSubmit={handleSearch}>
                <Input
                  type="text"
                  placeholder={t('nav.search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setShowResults(true)}
                  aria-label={t('nav.search')}
                  className="h-9 w-44 lg:w-56 rounded-lg border-hairline-strong bg-white/[0.07] pr-9 text-ink placeholder:text-ink-faint focus-visible:ring-brand"
                />
                <Search className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-faint" />
              </form>
              {showResults && searchResults.length > 0 && (
                <div className="absolute right-0 top-[calc(100%+8px)] w-80 rounded-2xl border border-hairline-strong bg-surface-raised p-1.5 shadow-[0_18px_38px_-12px_rgba(0,0,0,.8)]">
                  {searchResults.map((r) => (
                    <button
                      key={r.href}
                      onClick={() => handleResultClick(r.href)}
                      className="block w-full rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-white/[0.06]"
                    >
                      <span className="block text-sm font-semibold text-ink">{r.title}</span>
                      <span className="mt-0.5 block text-xs text-ink-faint">{r.description}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <LanguageSwitcher />

            <Link
              to={BRAND_NAV_CTA}
              className="hidden sm:inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-lg bg-brand px-4 text-[13px] font-bold text-ink-onBrand shadow-[0_8px_24px_-10px_rgba(20,184,166,.8)] transition-[background-color,transform] duration-200 ease-brand hover:bg-brand-600 active:scale-[0.97]"
            >
              {t('nav.ctaButton')}
            </Link>

            <Button
              variant="ghost"
              size="sm"
              className="p-2 text-ink hover:bg-white/[0.06] xl:hidden"
              aria-expanded={isMenuOpen}
              aria-label={isMenuOpen ? "Menü schließen" : "Menü öffnen"}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* ---- Untere Zeile: Hauptnavigation ---- */}
        <nav ref={navRef} className="hidden xl:flex items-center gap-1 h-[52px] -ml-3">
          {navigationItems.map((item, i) => (
            <div key={item.label} className="relative">
              <button
                type="button"
                aria-expanded={openDropdown === i}
                aria-haspopup="true"
                onClick={() => setOpenDropdown(openDropdown === i ? null : i)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-[13px] font-bold uppercase tracking-[0.06em] transition-colors duration-200 ${
                  isActive(item)
                    ? "bg-white/[0.07] text-ink"
                    : "text-ink-muted hover:bg-white/[0.06] hover:text-ink"
                }`}
              >
                {item.label}
                <svg
                  className={`h-2.5 w-2.5 transition-transform duration-200 ease-brand ${openDropdown === i ? "rotate-180" : ""}`}
                  viewBox="0 0 10 10"
                  fill="none"
                  aria-hidden="true"
                >
                  <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {/* Klapppanel: skaliert aus dem Ausloeser, nicht aus der Mitte */}
              <div
                role="menu"
                className={`absolute left-0 top-[calc(100%+6px)] min-w-[260px] origin-top-left rounded-2xl border border-hairline-strong bg-surface-raised p-1.5 shadow-[0_1px_2px_rgba(0,0,0,.4),0_18px_38px_-12px_rgba(0,0,0,.8)] transition-[opacity,transform] duration-200 ease-brand ${
                  openDropdown === i
                    ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
                    : "pointer-events-none -translate-y-0.5 scale-[0.97] opacity-0"
                }`}
              >
                {item.dropdown.map((d) =>
                  d.external ? (
                    <a
                      key={d.label}
                      href={d.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${linkClasses} flex items-center justify-between gap-3`}
                    >
                      {d.label}
                      <svg className="h-3 w-3 text-ink-faint" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                        <path d="M3.5 8.5l5-5M4.5 3.5h4v4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </a>
                  ) : (
                    <Link key={d.label} to={d.href} className={linkClasses}>
                      {d.label}
                    </Link>
                  )
                )}
              </div>
            </div>
          ))}
        </nav>
      </div>

      {/* ---- Mobilmenue ---- */}
      {isMenuOpen && (
        <nav className="absolute left-0 right-0 top-full max-h-[calc(100dvh-120px)] overflow-y-auto border-b border-hairline bg-surface px-6 pb-8 pt-2 xl:hidden">
          {navigationItems.map((item) => (
            <div key={item.label} className="border-b border-hairline py-3 last:border-b-0">
              <Link to={item.href} className="mb-1.5 block text-base font-bold text-ink">
                {item.label}
              </Link>
              <div className="pl-3 pt-1">
                {item.dropdown.map((d) =>
                  d.external ? (
                    <a
                      key={d.label}
                      href={d.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 py-2 text-sm text-ink-muted"
                    >
                      {d.label}
                      <svg className="h-3 w-3 text-ink-faint" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                        <path d="M3.5 8.5l5-5M4.5 3.5h4v4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </a>
                  ) : (
                    <Link key={d.label} to={d.href} className="block py-2 text-sm text-ink-muted">
                      {d.label}
                    </Link>
                  )
                )}
              </div>
            </div>
          ))}
          <Link
            to={BRAND_NAV_CTA}
            className="mt-6 inline-flex h-11 w-full items-center justify-center rounded-lg bg-brand font-bold text-ink-onBrand"
          >
            {t('nav.ctaButton')}
          </Link>
        </nav>
      )}
    </header>
  );
};

export default Header;
